/**
 * Auth Store — 관리자 로그인 상태 관리
 *
 * Mobius 인증 결과를 localStorage에 세션으로 저장.
 * API 토큰 없이 프론트엔드 접근 제어만 수행.
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

const SESSION_KEY = 'dronecast.authSession'
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000  // 8시간

interface AuthSession {
  email: string
  name: string
  mobiusUrl: string
  loginAt: number
  expiry: number
}

export const useAuthStore = defineStore('auth', () => {
  const email = ref<string | null>(null)
  const name = ref<string | null>(null)
  const mobiusUrl = ref<string | null>(null)

  const isLoggedIn = computed(() => !!email.value)

  /** 세션 복원 (앱 시작 시 호출) */
  function restoreSession() {
    try {
      const raw = localStorage.getItem(SESSION_KEY)
      if (!raw) return

      const session: AuthSession = JSON.parse(raw)
      if (Date.now() > session.expiry) {
        localStorage.removeItem(SESSION_KEY)
        return
      }

      email.value = session.email
      name.value = session.name
      mobiusUrl.value = session.mobiusUrl
    } catch {
      localStorage.removeItem(SESSION_KEY)
    }
  }

  /** 로그인 성공 시 호출 */
  function setLogin(data: { email: string; name: string; mobiusUrl: string }) {
    email.value = data.email
    name.value = data.name
    mobiusUrl.value = data.mobiusUrl

    const session: AuthSession = {
      email: data.email,
      name: data.name,
      mobiusUrl: data.mobiusUrl,
      loginAt: Date.now(),
      expiry: Date.now() + SESSION_DURATION_MS,
    }
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  }

  /** 로그아웃 */
  function logout() {
    email.value = null
    name.value = null
    mobiusUrl.value = null
    localStorage.removeItem(SESSION_KEY)
  }

  // 앱 시작 시 자동 복원
  restoreSession()

  return {
    email,
    name,
    mobiusUrl,
    isLoggedIn,
    setLogin,
    logout,
    restoreSession,
  }
})
